import React,
{
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { Image, Space } from 'antd';
import { SortOrder } from 'antd/es/table/interface';
import { useAppDispatch, useAppSelector } from '../../../hooks/redux';
import { useHistory } from 'react-router-dom';
import TagsSearch from '../../../components/tags/TagsSearch/TagsSearch';
import { PaginationContainer } from '../../../components/common/table/CustomTable.styles';
import CustomPagination from '../../../components/common/pagination/CustomPagination';
import ChangePageSize from '../../../components/common/pagination/ChangePageSize';
import { SpaceEnd, SubTitle, Tag, Text } from '../../../styles/common.styles';
import { TableData } from '../../../models/tableData/types';
import {
    ManufacturerText,
    ProductCell,
    ProductNameText,
    ProductTextLink,
    ReturnToTagLink,
    TableRow,
    TagGroup,
    TagListContainer,
    TagProductsTable,
    TagsTableSpace,
    UnselectAllButton,
}
    from './TagProductsTable.styles';
import { ProductType, TagProductRow, TagsTableProps } from './TagProductsTable.types';
import { buildTagProductRow, columnToSortExpression, parseExternalProducts, sortExpressionRecord } from './TagProductsTable.consts';
import TagProductActions from '../../common/tagProductActions/TagProductActions';
import { getCompaniesOfProducts, getExternalProductsAction } from '../../../redux/actions/transparencyCatalogActions';
import { BrandCompany, ExternalProduct } from '../../../models/product/types';
import { ActionResponse } from '../../../models/actionResponse/types';
import { GetProductsFromTransparencyCatalogAPIResponse } from '../../../redux/actions/transparencyCatalogActions/types';
import { TagsList } from '../../favoritProducts/favoriteProductItem/favoriteNoteSection/FavoriteNoteSection.styles';
import { toShortLocalDate } from '../../../helpers/dateHelper';
import { blueLink } from '../../../styles/colors';
import { TagsSectionSpinner } from '../TagsSection/TagsSection.styles';
import { bulkDeleteProjectProductTagsAction } from '../../../redux/actions/projectProductTagActions';
import { bulkDeleteFavoriteProductTagsAction } from '../../../redux/actions/favoriteProductTagActions';
import { removeProductsFromTagCache } from '../../../redux/reducers/tagReducer/TagSlice';
import { TableRowLink } from '../TagsTable/TagsTable.styles';
import { ShortTag } from '../../../redux/actions/tagActions/types';
import { splitAndSortShortTags } from '../../../utils/tagUtils';
import { TagsSubTitle } from '../CreateTag/CreateTag.styles';
import { usePermissions } from '../../../hooks/permissions';
import { AccountPermissionTypes } from '../../../models/permission/enums';

const TagsProductsTable: React.FC<TagsTableProps> = ({
    tagId, setTableData, tableData, setSelectedProductIds, selectedProductIds }) => {

    const sortDirections: SortOrder[] = ['ascend', 'descend'];

    const dispatch = useAppDispatch();
    const organizationName = useAppSelector((state) => state.authReducer.user?.organization?.name);
    const { productsByTagId } = useAppSelector((state) => state.tagReducer);
    const [externalById, setExternalById] = useState<Record<number, ExternalProduct>>({});
    const [companyById, setCompanyById] = useState<Record<number, BrandCompany>>({});
    const [enrichmentLoading, setEnrichmentLoading] = useState(true);
    const history = useHistory();
    const { getAccountRole } = usePermissions();
    const isUserAdmin = getAccountRole() === AccountPermissionTypes.ACCOUNT_ADMIN


    const tagEntry = productsByTagId[tagId];
    const tagDataPending = !tagEntry || tagEntry.pending;
    const loading = enrichmentLoading || tagDataPending;
    const rawProducts = tagEntry?.products || [];

    const productIds = useMemo(
        () => [...new Set(rawProducts.map((p) => p.productId).filter((id): id is number => id != null))],
        [rawProducts],
    );

    useEffect(() => {
        const loadEnrichment = async () => {
            setEnrichmentLoading(true);
            if (!productIds.length) {
                setExternalById({});
                setCompanyById({});
                return;
            }

            const { payload } = (await dispatch(
                getExternalProductsAction({ productIds }),
            )) as ActionResponse<GetProductsFromTransparencyCatalogAPIResponse>;

            const externalProducts = parseExternalProducts(payload?.data);
            const externalMap = externalProducts.reduce((acc, item) => {
                acc[item.id] = item;
                return acc;
            }, {} as Record<number, ExternalProduct>);
            setExternalById(externalMap);

            const companies = await getCompaniesOfProducts(externalProducts, dispatch);
            const companyMap = companies.reduce((acc, item) => {
                acc[item.id] = item;
                return acc;
            }, {} as Record<number, BrandCompany>);
            setCompanyById(companyMap);
        };

        loadEnrichment().finally(() => setEnrichmentLoading(false));
    }, [dispatch, productIds]);

    const changePageNumber = (pageNumber: number) => {
        const newTableData = { ...tableData, curPage: pageNumber }
        setTableData(newTableData);
    };

    const changePageSize = (newPageSize: number) => {
        const newTableData = { ...tableData, pageSize: newPageSize, curPage: 1 };
        setTableData(newTableData);
    };

    const handleCurrentPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        const currentCount = event.currentTarget.value;
        const visibleProductsCount = filteredProducts.length;
        if (/^-?\d*\.?\d*$/.test(currentCount) || currentCount === '') {
            setTableData({ ...tableData, curPage: +event.currentTarget.value });
            if (+currentCount > Math.ceil(visibleProductsCount / tableData.pageSize)) {
                setTimeout(() => {
                    const newCur = Math.ceil(visibleProductsCount / tableData.pageSize);
                    setTableData({ ...tableData, curPage: newCur });
                    changePageNumber(newCur);
                }, 1000);
            }
        }
        if (
            +event.currentTarget.value > 0 &&
            +event.currentTarget.value <= Math.ceil(visibleProductsCount / tableData.pageSize)
        ) {
            changePageNumber(+event.currentTarget.value);
        }
    };

    const handleSearchChange = useCallback((value: string) => {
        setTableData((prev) => ({ ...prev, searchString: value }));
    }, []);

    const products = useMemo(() => {
        return rawProducts.map((item) => buildTagProductRow({ item, externalById, companyById, organizationName }));
    }, [rawProducts, externalById, companyById, organizationName]);

    const filteredProducts = useMemo(() => {
        const search = (tableData.searchString || '').trim().toLowerCase();
        if (!search) {
            return products;
        }

        return products.filter((item) =>
            item.nameSort.toLowerCase().includes(search) ||
            item.tagsSort.toLowerCase().includes(search) ||
            item.projectLibrarySort.toLowerCase().includes(search) ||
            item.addedBy.toLowerCase().includes(search) ||
            (item.catalogUrl || '').toLowerCase().includes(search) ||
            String(item.id).includes(search),
        );
    }, [products, tableData]);

    const sortedProducts = useMemo(() => {
        const direction = tableData.sortDirection === 'desc' ? -1 : 1;
        const list = [...filteredProducts];

        switch (tableData.sortExpression) {
            case columnToSortExpression.name:
                return list.sort((a, b) => a.nameSort.localeCompare(b.nameSort) * direction);
            case columnToSortExpression.productCount:
                return list.sort((a, b) => (a.productCount - b.productCount) * direction);
            case columnToSortExpression.projectLibrary:
                return list.sort((a, b) => a.projectLibrarySort.localeCompare(b.projectLibrarySort) * direction);
            case columnToSortExpression.createdOn:
                return list.sort((a, b) => a.createdAtSortable.localeCompare(b.createdAtSortable) * direction);
            case columnToSortExpression.createdByName:
                return list.sort((a, b) => a.addedBy.localeCompare(b.addedBy) * direction);
            default:
                return list;
        }
    }, [filteredProducts, tableData.sortDirection, tableData.sortExpression]);

    const paginatedProducts = useMemo(() => {
        const start = (tableData.curPage - 1) * tableData.pageSize;
        return sortedProducts.slice(start, start + tableData.pageSize);
    }, [sortedProducts, tableData.curPage, tableData.pageSize]);

    const handleSearchSubmit = useCallback((_e: React.KeyboardEvent<HTMLInputElement>) => {
        setTableData((prev) => {
            return { ...prev, curPage: 1 };
        });
    }, []);

    const handleSearchClear = useCallback(() => {
        setTableData((prev) => {
            return { ...prev, searchString: '', curPage: 1 };
        });
    }, []);

    const handleSelectAll = (e: any) => {
        if (e.target.checked) {
            setSelectedProductIds?.([...new Set(filteredProducts.map((p) => p.id))]);
        } else {
            setSelectedProductIds?.([]);
        }
    };

    const handleTableChange = (_pagination: any, _filters: any, sorter: any) => {
        const sortDir = sorter.order === 'descend' ? 'desc' : 'asc';
        const sortExpr = sorter.order ? (sortExpressionRecord[sorter.field]) : null;
        const newTableData = { ...tableData, sortDirection: sortDir, sortExpression: sortExpr, curPage: 1 } as TableData;
        setTableData(newTableData);
    };

    const tagColumns = [
        {
            title: 'MFR / Product',
            dataIndex: 'nameSort',
            key: 'name',
            width: 340,
            sorter: true,
            sortDirections,
            showSorterTooltip: false,
            render: (_value: string, row: TagProductRow) => (
                <ProductCell>
                    {row.catalogUrl ? (
                        <ProductTextLink href={row.catalogUrl} target="_blank" rel="noopener noreferrer">
                            <ManufacturerText as="span" style={{ color: 'inherit' }}>{row.manufacturer}</ManufacturerText>
                            {" "}
                            <ProductNameText as="span" style={{ color: 'inherit' }}>{row.productName}</ProductNameText>
                        </ProductTextLink>
                    ) : (
                        <div>
                            <ManufacturerText>{row.manufacturer}</ManufacturerText>
                            {" "}
                            <ProductNameText>{row.productName}</ProductNameText>
                        </div>
                    )}
                </ProductCell>
            ),
        },
        {
            title: 'Tags',
            dataIndex: 'tags',
            key: 'tags',
            width: 340,
            render: (tags: ShortTag[]) => (
                <TagsList>
                    {splitAndSortShortTags(tags ?? []).map((t) => (
                        <Tag
                            key={t.tagId}
                            color={t?.display}
                            style={{ borderColor: t?.baseColor }}
                        >
                            {t?.name}
                        </Tag>
                    ))}
                </TagsList>
            ),
        },
        {
            title: 'Project / library',
            dataIndex: 'projectLibraryText',
            key: 'projectLibrary',
            sorter: true,
            sortDirections,
            showSorterTooltip: false,
            render: (_value: string, row: TagProductRow) => {
                return <TableRowLink
                    onClick={() => history.push(row.projectLibraryLink)}
                >{row.projectLibraryText}</TableRowLink>;
            },
        },
        {
            title: 'Date tag added',
            dataIndex: 'dateTagAdded',
            key: 'dateTagAdded',
            sorter: true,
            sortDirections,
            showSorterTooltip: false,
            render: (date: string | undefined) =>
                <TableRow>{toShortLocalDate(date)}</TableRow>
        },
        {
            title: 'Added by',
            dataIndex: 'addedBy',
            key: 'addedBy',
            sorter: true,
            sortDirections,
            showSorterTooltip: false,
        }
    ];

    const rowSelection = {
        selectedRowKeys: selectedProductIds,
        onChange: (selectedRowKeys: React.Key[]) => {
            setSelectedProductIds?.(selectedRowKeys as number[]);
        }
    };

    const selectedProductsCount = selectedProductIds?.length ?? 0;
    const visibleCount = filteredProducts.length;

    const handleProductTagDelete = async () => {
        const productsToDelete = sortedProducts.filter((p) => selectedProductIds?.includes(p.key));

        const { favoriteIds, projectIds } = productsToDelete.reduce<{
            favoriteIds: number[];
            projectIds: number[];
        }>(
            (acc, p) => {
                const association = (p.tags || []).find(
                    (t: ShortTag) => t.tagId === tagId && t.associationId != null,
                );
                if (!association?.associationId) {
                    return acc;
                }

                if (p.productType === ProductType.FavoriteProduct) {
                    acc.favoriteIds.push(association.associationId);
                } else if (p.productType === ProductType.ProjectProduct) {
                    acc.projectIds.push(association.associationId);
                }
                return acc;
            },
            { favoriteIds: [], projectIds: [] },
        );

        dispatch(bulkDeleteFavoriteProductTagsAction(favoriteIds));
        dispatch(bulkDeleteProjectProductTagsAction(projectIds));
        dispatch(removeProductsFromTagCache({ tagId, productIds: selectedProductIds ?? [] }));
        setSelectedProductIds?.([]);
    };

    return <TagsSectionSpinner spinning={loading} size="large">
        <TagListContainer>
            <TagsTableSpace direction="horizontal" className='align-top'>
                <Space direction="vertical">
                    <ReturnToTagLink
                        to={`/tags`}
                    >
                        <Image src="src/assets/images/ic-play-blue.svg" preview={false} color={blueLink} />
                        <Text weight={400} height={18} size={14} color={blueLink}>
                            Return to Tags page
                        </Text>
                    </ReturnToTagLink>
                </Space>
                <SpaceEnd>
                    {isUserAdmin && (
                        <TagProductActions
                            selectedCount={selectedProductsCount}
                            disabled={selectedProductsCount === 0}
                            handleDelete={handleProductTagDelete}
                        />
                    )}
                </SpaceEnd>
            </TagsTableSpace>
            <TagsSubTitle>
                Products in Projects and your Organization’s Favorite Products Library use this tag.<br />
                Products tagged in Personal Libraries are not displayed.
            </TagsSubTitle>
            <TagsTableSpace direction="horizontal">
                <Space direction="vertical">
                    <TagGroup>
                        <TagsSearch
                            placeholder='Product, Project, Library, tag'
                            searchString={tableData.searchString}
                            changeSearch={handleSearchChange}
                            cleanFilter={handleSearchClear}
                            handleSearchSubmit={handleSearchSubmit} />
                        <SubTitle>
                            {visibleCount} products
                        </SubTitle>
                        {selectedProductsCount > 0 && (
                            <UnselectAllButton onClick={() => handleSelectAll({ target: { checked: false } })}>
                                Unselect all
                            </UnselectAllButton>
                        )}
                    </TagGroup>
                </Space>
                <SpaceEnd>
                    <ChangePageSize changePageSize={changePageSize} pageSize={tableData.pageSize} />
                    <PaginationContainer style={{ marginLeft: 16 + `px` }}>
                        <CustomPagination
                            page={tableData.curPage}
                            total={visibleCount}
                            pageSize={tableData.pageSize}
                            changePageNumber={changePageNumber}
                            handleCurrentPage={handleCurrentPage}
                        />
                    </PaginationContainer>
                </SpaceEnd>
            </TagsTableSpace>
            <TagProductsTable
                readOnly={!isUserAdmin}
                onChange={handleTableChange}
                rowSelection={isUserAdmin ? {
                    type: 'checkbox',
                    ...rowSelection,
                    columnWidth: 16,
                } : undefined}
                dataSource={paginatedProducts}
                columns={tagColumns as any}
                pagination={false}
            />
            <SpaceEnd>
                <PaginationContainer style={{ marginLeft: 16 + `px`, marginTop: 16 + `px` }}>
                    <CustomPagination
                        page={tableData.curPage}
                        total={visibleCount}
                        pageSize={tableData.pageSize}
                        changePageNumber={changePageNumber}
                        handleCurrentPage={handleCurrentPage}
                    />
                </PaginationContainer>
            </SpaceEnd>
        </TagListContainer>
    </TagsSectionSpinner>
}
export default TagsProductsTable;
